// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Universe Configuration
 * 
 * Defines all available universes, their metadata, and relationships.
 * Each universe has its own star systems, gates, and theme.
 */

export const universes = {
  milkyway: {
    id: 'milkyway',
    name: 'Milky Way',
    codename: 'ORIGIN',
    description: 'Birthplace of humanity. Known space. The stars of home.',
    theme: {
      primary: '#00ffff',      // Cyan
      secondary: '#0088ff',    // Blue
      accent: '#00ff88',       // Green
      background: 'rgba(0, 20, 40, 0.95)'
    },
    discovered: true, // Always visible from start
    startSystem: 0, // SOL (EARTH)
    // Galaxy map coordinates (relative position in multiverse)
    galaxyPosition: { x: 30, y: 50 } // Center-left
  },
  'dungeon-dimensions': {
    id: 'dungeon-dimensions',
    name: 'Dungeon Dimensions',
    codename: 'ELDRITCH',
    description: 'A realm of eldritch horror and impossible mathematics. Where Things Man Was Not Meant To Know lurk, and reality becomes... negotiable.',
    theme: {
      primary: '#8B008B',      // Dark magenta
      secondary: '#4B0082',    // Indigo
      accent: '#9932CC',       // Dark orchid
      background: 'rgba(40, 0, 40, 0.95)'
    },
    discovered: false, // Must find gate to discover
    startSystem: 100, // First system ID in dungeon dimensions universe
    // Galaxy map coordinates (relative position in multiverse)
    galaxyPosition: { x: 70, y: 50 } // Center-right
  },
  darksector: {
    id: 'darksector',
    name: 'Dark Sector',
    codename: 'VOID',
    description: 'Dying universe on the edge of heat death. Scarce resources. Extreme danger. The stars are going out.',
    theme: {
      primary: '#ff4444',      // Red
      secondary: '#ff8800',    // Orange
      accent: '#ffaa44',       // Yellow-orange
      background: 'rgba(40, 0, 0, 0.95)'
    },
    discovered: false,
    startSystem: 200, // First system ID in darksector universe
    // Galaxy map coordinates (relative position in multiverse)
    galaxyPosition: { x: 50, y: 20 } // Top-center
  },
  improbability: {
    id: 'improbability',
    name: 'Improbability Sector',
    codename: 'ABSURD',
    description: 'A universe where the laws of physics are more like polite suggestions. Probability fields are unstable. Reality is optional. Don\'t forget your towel.',
    theme: {
      primary: '#ffff00',      // Yellow
      secondary: '#00ff00',    // Green
      accent: '#ff00ff',       // Magenta
      background: 'rgba(20, 20, 0, 0.95)'
    },
    discovered: false,
    startSystem: 300, // First system ID in improbability universe
    // Galaxy map coordinates (relative position in multiverse)
    galaxyPosition: { x: 50, y: 80 } // Bottom-center
  },
  discworld: {
    id: 'discworld',
    name: 'Discworld',
    codename: 'NARRATIVE',
    description: 'A flat world carried by four elephants on the back of Great A\'Tuin. Where magic works, narrative causality is real, and the Librarian goes "Ook!"',
    theme: {
      primary: '#8B4513',      // Brown (earth/disc)
      secondary: '#228B22',    // Forest green
      accent: '#FFD700',       // Gold (magic)
      background: 'rgba(20, 15, 10, 0.95)'
    },
    discovered: false,
    startSystem: 500, // First system ID in discworld universe
    // Galaxy map coordinates (relative position in multiverse)
    galaxyPosition: { x: 20, y: 30 } // Left side
  },
  lspace: {
    id: 'lspace',
    name: 'L-Space',
    codename: 'LIBRARY',
    description: 'Library Space - where all libraries are connected across space and time. A dimension of pure knowledge. Ook!',
    theme: {
      primary: '#8B4513',      // Brown (books)
      secondary: '#FFD700',    // Gold (knowledge)
      accent: '#DEB887',       // Burlywood (parchment)
      background: 'rgba(25, 20, 15, 0.95)'
    },
    discovered: false,
    startSystem: 800, // First system ID in lspace universe
    // Galaxy map coordinates (relative position in multiverse)
    galaxyPosition: { x: 15, y: 45 } // Left side, near Discworld
  },
  pokemon: {
    id: 'pokemon',
    name: 'Pokémon Universe',
    codename: 'POCKET',
    description: 'A universe where Pokémon have evolved across different planets. Each region is now a star system with unique ecosystems. Gotta explore them all!',
    theme: {
      primary: '#FF0000',      // Pokéball red
      secondary: '#FFDE00',    // Pikachu yellow
      accent: '#3B4CCA',       // Pokéball blue
      background: 'rgba(30, 10, 10, 0.95)'
    },
    discovered: false,
    startSystem: 600, // Kanto Prime
    // Galaxy map coordinates (relative position in multiverse)
    galaxyPosition: { x: 35, y: 55 } // Near Milky Way (anchored)
  },
  neonmetropolis: {
    id: 'neonmetropolis',
    name: 'Neon Metropolis',
    codename: 'CHROME',
    description: 'Megacorporations rule. Neon bleeds through acid rain. Humanity upgraded, humanity lost.',
    theme: {
      primary: '#00ffff',
      secondary: '#ff00ff',
      accent: '#ffff00',
      background: 'rgba(10, 0, 20, 0.95)'
    },
    discovered: false,
    startSystem: 700,
    galaxyPosition: { x: 60, y: 35 }
  },
  digiworld: {
    id: 'digiworld',
    name: 'Digital World',
    codename: 'DIGI',
    description: 'A parallel digital dimension. Data streams form landscapes. Digimon evolve and devolve.',
    theme: {
      primary: '#00ffff',
      secondary: '#ff00ff',
      accent: '#00ff00',
      background: 'rgba(0, 10, 30, 0.95)'
    },
    discovered: false,
    startSystem: 900,
    galaxyPosition: { x: 40, y: 60 }
  },
  marioworld: {
    id: 'marioworld',
    name: 'Mushroom Kingdom',
    codename: 'WAHOO',
    description: 'A universe of pipes, power-ups, and princesses. Gravity is optional. Coins are everywhere. Let\'s-a-go!',
    theme: {
      primary: '#FF0000',
      secondary: '#00FF00',
      accent: '#FFFF00',
      background: 'rgba(30, 20, 10, 0.95)'
    },
    discovered: false,
    startSystem: 1000,
    galaxyPosition: { x: 30, y: 50 }
  },
  sonicuniverse: {
    id: 'sonicuniverse',
    name: 'Sonic Universe',
    codename: 'SPEED',
    description: 'Gotta go fast! A universe of rings, loops, and chaos emeralds. Speed is life.',
    theme: {
      primary: '#0066FF',
      secondary: '#FFCC00',
      accent: '#FF0000',
      background: 'rgba(10, 30, 20, 0.95)'
    },
    discovered: false,
    startSystem: 1100,
    galaxyPosition: { x: 25, y: 55 }
  },
  zelda: {
    id: 'zelda',
    name: 'Hyrule',
    codename: 'TRIFORCE',
    description: 'A land of legend. The Triforce awaits. Courage, Wisdom, Power.',
    theme: {
      primary: '#00AA00',
      secondary: '#FFD700',
      accent: '#0066FF',
      background: 'rgba(20, 30, 10, 0.95)'
    },
    discovered: false,
    startSystem: 1200,
    galaxyPosition: { x: 20, y: 50 }
  },
  starwars: {
    id: 'starwars',
    name: 'Star Wars Galaxy',
    codename: 'FARAWAY',
    description: 'A galaxy far, far away. The Force binds all things. Light and dark in eternal conflict.',
    theme: {
      primary: '#FFE81F',      // Star Wars yellow
      secondary: '#3b82f6',    // Blue
      accent: '#dc2626',       // Red (lightsaber)
      background: 'rgba(0, 0, 20, 0.95)'
    },
    discovered: false,
    startSystem: 800,
    galaxyPosition: { x: 65, y: 35 }
  },
  doctorwho: {
    id: 'doctorwho',
    name: 'Whoniverse',
    codename: 'TARDIS',
    description: 'All of time and space. Wibbly-wobbly, timey-wimey. The Doctor\'s universe.',
    theme: {
      primary: '#003B6F',      // TARDIS blue
      secondary: '#d97706',    // Gallifrey orange
      accent: '#00ffff',       // Cyan
      background: 'rgba(0, 20, 40, 0.95)'
    },
    discovered: false,
    startSystem: 900,
    galaxyPosition: { x: 70, y: 60 }
  },
  startrek: {
    id: 'startrek',
    name: 'Star Trek Universe',
    codename: 'FEDERATION',
    description: 'The Final Frontier. To boldly go where no one has gone before. United Federation of Planets.',
    theme: {
      primary: '#3b82f6',      // Starfleet blue
      secondary: '#fbbf24',    // Command gold
      accent: '#dc2626',       // Alert red
      background: 'rgba(0, 20, 40, 0.95)'
    },
    discovered: false,
    startSystem: 1000,
    galaxyPosition: { x: 60, y: 40 }
  },
  masseffect: {
    id: 'masseffect',
    name: 'Mass Effect Galaxy',
    codename: 'CITADEL',
    description: 'The galaxy of Mass Effect. Citadel Council. Mass relays. Reapers lurk in dark space.',
    theme: {
      primary: '#00d4ff',      // Mass relay blue
      secondary: '#0088ff',    // Citadel blue
      accent: '#ff0000',       // Renegade red
      background: 'rgba(0, 20, 40, 0.95)'
    },
    discovered: false,
    startSystem: 900,
    galaxyPosition: { x: 55, y: 45 }
  },
  halo: {
    id: 'halo',
    name: 'Halo Universe',
    codename: 'UNSC',
    description: 'UNSC space. Covenant war. Forerunner legacy. Spartans never die.',
    theme: {
      primary: '#0066cc',      // UNSC blue
      secondary: '#004499',    // Navy blue
      accent: '#00ff88',       // Forerunner green
      background: 'rgba(0, 20, 40, 0.95)'
    },
    discovered: false,
    startSystem: 1000,
    galaxyPosition: { x: 50, y: 35 }
  },
  warhammer40k: {
    id: 'warhammer40k',
    name: 'Warhammer 40,000',
    codename: 'IMPERIUM',
    description: 'In the grim darkness of the far future, there is only war. The Emperor protects.',
    theme: {
      primary: '#ffaa00',      // Imperial gold
      secondary: '#ff0000',    // Blood red
      accent: '#000000',       // Grimdark black
      background: 'rgba(20, 0, 0, 0.95)'
    },
    discovered: false,
    startSystem: 1100,
    galaxyPosition: { x: 45, y: 30 }
  },
  marvel: {
    id: 'marvel',
    name: 'Marvel Universe',
    codename: 'EARTH-616',
    description: 'Earth-616. Avengers assemble. Infinity Stones. Multiversal madness.',
    theme: {
      primary: '#ff0000',      // Marvel red
      secondary: '#0044cc',    // Marvel blue
      accent: '#ffaa00',       // Gold
      background: 'rgba(20, 0, 0, 0.95)'
    },
    discovered: false,
    startSystem: 1200,
    galaxyPosition: { x: 40, y: 55 }
  },
  dc: {
    id: 'dc',
    name: 'DC Universe',
    codename: 'EARTH-PRIME',
    description: 'Earth-Prime. Justice League. Speed Force. Crisis on Infinite Earths.',
    theme: {
      primary: '#0044cc',      // DC blue
      secondary: '#ff0000',    // DC red
      accent: '#ffaa00',       // Gold
      background: 'rgba(0, 0, 20, 0.95)'
    },
    discovered: false,
    startSystem: 1300,
    galaxyPosition: { x: 35, y: 60 }
  }
};

// Universe metadata for UI display
export const getUniverseInfo = (universeId) => {
  return universes[universeId] || universes.milkyway;
};

// Check if universe is discovered
export const isUniverseDiscovered = (universeId, discoveredUniverses = ['milkyway']) => {
  return discoveredUniverses.includes(universeId);
};

// Get all discovered universes
export const getDiscoveredUniverses = (discoveredUniverses = ['milkyway']) => {
  return Object.values(universes).filter(u => discoveredUniverses.includes(u.id));
};
